import {
  And,
  Annotation,
  BoundingBox,
  Color,
  Concept,
  ConceptRelation,
  Data,
  EmailAddress,
  EvalMetrics,
  Filter,
  HostedURL,
  Image,
  ImageInfo,
  Input,
  InputInfo,
  Mask,
  MetricsSummary,
  Model,
  ModelTypeEnumOption,
  ModelTypeField,
  ModelTypeRangeInfo,
  ModelVersion,
  NodeInput,
  Output,
  OutputConfig,
  OutputInfo,
  Polygon,
  RedirectInfo,
  Region,
  RegionInfo,
  Search,
  Status,
  TaskStatusCountPerUser,
  TextInfo,
  Timestamp,
  User,
  UserDetail,
  VideoInfo,
  Visibility,
  Workflow,
  WorkflowNode,
  WorkflowVersion,
} from 'api/grpc/resources';
import { StatusCode } from 'grpc-web';
import * as google_protobuf_struct_pb from 'google-protobuf/google/protobuf/struct_pb';

import { grpcToRestPointData } from 'utils/annotations/point';

/**
 * Converts a gRPC Input to a REST Input
 * @param input - The gRPC Input object to convert
 * @returns The converted REST Input object
 * @note This function currently has known issues with missing data handling.
 * The current implementation may mask underlying problems and needs to be re-evaluated.
 * TODO: Revisit data handling to properly handle missing/invalid data cases.
 */
export function grpcToRestInput(input: Input): CF.API.Inputs.Input {
  const data = input.getData();

  return {
    id: input.getId(),
    created_at: input.getCreatedAt()?.toDate().toISOString() || '',
    modified_at: input.getModifiedAt()?.toDate().toISOString() || '',
    status: grpcToRestStatus(input.getStatus()),
    // NOTE: Using empty object as fallback is intentional here
    // - We don't want to assume a specific type (like TextInputData) as it might be a type we don't handle yet
    // - This allows the conversion to succeed even with broken/missing data from backend
    // - The empty object is the safest fallback that won't break type checking
    data: data ? grpcToRestData(data) : ({} as CF.API.Inputs.AnyInputData),
  };
}

/**
 * Converts a gRPC Input to a REST Input with optional fields
 * @param input - The gRPC Input object to convert
 * @returns The converted REST Input object with optional fields
 */
export function grpcToRestInputOptional(input: Input): Partial<CF.API.Inputs.Input> {
  const inputData = input.getData();
  const inputStatus = input.getStatus();

  return {
    id: input.getId(),
    data: inputData ? grpcToRestData(inputData) : ({} as CF.API.Inputs.AnyInputData),
    created_at: input.getCreatedAt()?.toDate().toISOString() || undefined,
    modified_at: input.getModifiedAt()?.toDate().toISOString() || undefined,
    status: inputStatus ? grpcToRestStatus(inputStatus) : undefined,
    dataset_ids: input.getDatasetIdsList() || [],
  };
}

/**
 * Converts a gRPC Image to a REST Image
 * @param image - The gRPC Image object to convert
 * @returns The converted REST Image object or undefined
 */
export const grpcToRestImage = (image?: Image): CF.API.Image | undefined => {
  if (!image) {
    return undefined;
  }

  return {
    url: image.getUrl(),
    base64: image.getBase64_asB64(),
    hosted: {
      crossorigin: image.getHosted()?.getCrossorigin(),
      sizes: image.getHosted()?.getSizesList(),
      prefix: image.getHosted()?.getPrefix(),
      suffix: image.getHosted()?.getSuffix(),
    },
  };
};

/**
 * Converts a gRPC RegionData to a REST RegionData
 * @param regionData - The gRPC RegionData object to convert
 * @returns The converted REST RegionData object
 * @note This function currently has known issues with missing data handling.
 * The current implementation may mask underlying problems and needs to be re-evaluated.
 * TODO: Revisit data handling to properly handle missing/invalid data cases.
 */
export const grpcToRestRegionData = (regionData?: Data): CF.API.RegionDataEntity => {
  if (!regionData) {
    return {
      concepts: undefined,
      text: undefined,
    };
  }

  const rawText = regionData.getText()?.getRaw();
  return {
    concepts: regionData.getConceptsList().map(grpcToRestConcept),
    text: rawText ? { raw: rawText } : undefined,
  };
};

/**
 * Converts a gRPC BoundingBox to a REST BoundingBox
 * @param boundingBox - The gRPC BoundingBox object to convert
 * @returns The converted REST BoundingBox object
 * @note This function currently has known issues with missing data handling.
 * The current implementation may mask underlying problems and needs to be re-evaluated.
 * TODO: Revisit data handling to properly handle missing/invalid data cases.
 */
export const grpcToRestBoundingBoxData = (boundingBox?: BoundingBox): CF.API.BoundingBox | undefined => {
  if (!boundingBox) {
    return undefined;
  }

  return {
    top_row: boundingBox.getTopRow(),
    right_col: boundingBox.getRightCol(),
    bottom_row: boundingBox.getBottomRow(),
    left_col: boundingBox.getLeftCol(),
  };
};

/**
 * Converts a gRPC Polygon to a REST Polygon
 * @param polygon - The gRPC Polygon object to convert
 * @returns The converted REST Polygon object
 * @note This function currently has known issues with missing data handling.
 * The current implementation may mask underlying problems and needs to be re-evaluated.
 * TODO: Revisit data handling to properly handle missing/invalid data cases.
 */
export const grpcToRestPolygonData = (polygon?: Polygon): CF.API.Polygon | undefined => {
  if (!polygon) {
    return undefined;
  }

  return {
    points: polygon.getPointsList().map((point) => point.toObject()),
  };
};

/**
 * Converts a gRPC Mask to a REST Mask
 * @param mask - The gRPC Mask object to convert
 * @returns The converted REST Mask object
 * @note This function currently has known issues with missing data handling.
 * The current implementation may mask underlying problems and needs to be re-evaluated.
 * TODO: Revisit data handling to properly handle missing/invalid data cases.
 */
export const grpcToRestMaskData = (mask?: Mask): CF.API.Mask | undefined => {
  if (!mask) {
    return undefined;
  }

  const maskImage = mask.getImage();

  return {
    image: {
      base64: maskImage?.getBase64_asB64(),
      url: maskImage?.getUrl(),
    },
  };
};

/**
 * Converts a gRPC RegionInfo to a REST RegionInfo
 * @param regionInfo - The gRPC RegionInfo object to convert
 * @returns The converted REST RegionInfo object or undefined
 */
export const grpcToRestRegionInfo = (regionInfo?: RegionInfo): CF.API.RegionInfo | undefined => {
  if (!regionInfo) {
    return undefined;
  }

  if (regionInfo.hasBoundingBox()) {
    const bounding_box = grpcToRestBoundingBoxData(regionInfo.getBoundingBox());
    return bounding_box ? { bounding_box } : undefined;
  }

  if (regionInfo.hasPolygon()) {
    const polygon = grpcToRestPolygonData(regionInfo.getPolygon());
    return polygon ? { polygon } : undefined;
  }

  if (regionInfo.hasMask()) {
    const mask = grpcToRestMaskData(regionInfo.getMask());
    return mask ? { mask } : undefined;
  }

  if (regionInfo.hasPoint()) {
    const point = grpcToRestPointData(regionInfo.getPoint());
    return point ? { point } : undefined;
  }
  return undefined;
};

/**
 * Converts a gRPC Region to a REST Region
 * @param region - The gRPC Region object to convert
 * @returns The converted REST Region object
 * @note This function currently has known issues with missing data handling.
 * The current implementation may mask underlying problems and needs to be re-evaluated.
 * TODO: Revisit data handling to properly handle missing/invalid data cases.
 */
export const grpcToRestRegion = (region: Region): CF.API.RegionsEntity => {
  const regionInfo = grpcToRestRegionInfo(region.getRegionInfo());

  return {
    id: region.getId(),
    data: grpcToRestRegionData(region.getData()),
    // NOTE: Using empty object as fallback is intentional here
    // - If we have region_info, it will contain bbox/polygon/mask
    // - If we don't have region_info, it's likely a classification type
    // - Empty object is the safest fallback that won't break type checking
    region_info: regionInfo || ({} as CF.API.RegionInfo),
    value: region.getValue(),
  };
};

/**
 * Converts a gRPC Color to a REST Color
 * @param color - The gRPC Color object to convert
 * @returns The converted REST Color object
 * @note This function currently has known issues with missing data handling.
 * The current implementation may mask underlying problems and needs to be re-evaluated.
 * TODO: Revisit data handling to properly handle missing/invalid data cases.
 */
export const grpcToRestColor = (color?: Color): CF.API.ColorsEntity => {
  if (!color) {
    return {
      raw_hex: '',
      value: 0,
      w3c: { hex: '', name: '' },
    };
  }

  const w3c = color.getW3c();

  return {
    raw_hex: color.getRawHex() || '',
    value: color.getValue() || 0,
    w3c: {
      hex: w3c?.getHex() || '',
      name: w3c?.getName() || '',
    },
  };
};

/**
 * Converts a gRPC Data to a REST TOutputData (AnnotationData)
 * @param data - The gRPC Data object to convert
 * @returns The converted REST TOutputData object or undefined
 */
export const grpcToRestTOutputData = (data?: Data): CF.API.TOutputData | undefined => {
  if (data?.hasImage()) {
    const image = data.getImage();
    if (!image) return undefined;

    const imageInfo = image.getImageInfo();
    return {
      image: {
        base64: image.getBase64_asB64(),
        image_info: {
          width: imageInfo ? imageInfo.getWidth() : undefined,
          height: imageInfo ? imageInfo.getHeight() : undefined,
          format: imageInfo ? imageInfo.getFormat() : '',
          color_mode: imageInfo ? imageInfo.getColorMode() : '',
        },
      },
    };
  }
  const framesList = data?.getFramesList();
  if (framesList && framesList.length > 0) {
    return {
      frames: framesList.map((frame) => ({
        data: (grpcToRestTOutputData(frame.getData()) as CF.API.BaseOutputData) ?? ({} as CF.API.BaseOutputData),
        frame_info: frame.getFrameInfo()?.toObject() ?? { index: 0, time: 0 },
        id: frame.getId(),
      })),
      metadata: (() => {
        const metadata = data?.getMetadata();
        return metadata ? protoStructToRecord(metadata) : {};
      })(),
    };
  }

  const regionList = data?.getRegionsList();
  if (regionList?.length) {
    return {
      regions: regionList.map(grpcToRestRegion),
    };
  }

  const conceptList = data?.getConceptsList();
  if (conceptList?.length) {
    return {
      concepts: conceptList.map(grpcToRestConcept),
    };
  }

  const colorList = data?.getColorsList();
  if (colorList?.length) {
    return {
      colors: colorList.map(grpcToRestColor),
    };
  }

  if (data?.hasText()) {
    return {
      text: {
        raw: data.getText()?.getRaw() || '',
      },
    };
  }

  return undefined;
};

/**
 * Converts a gRPC OutputInfo to a REST OutputInfo for workflows
 * @param outputInfo - The gRPC OutputInfo object to convert
 * @returns The converted REST OutputInfo object or undefined
 * @note The 'type' and 'type_ext' fields are REST-specific and don't have direct gRPC mappings.
 * These fields are set to empty strings as there is no corresponding gRPC field to map from.
 */
export const grpcToRestOutputInfoWorkflow = (outputInfo: OutputInfo | undefined): CF.API.ModelOutputs.OutputInfo | undefined =>
  outputInfo
    ? {
        message: outputInfo.getMessage(),
        output_config: grpcToRestModelsOutputConfig(outputInfo.getOutputConfig()) || ({} as CF.API.ModelOutputs.OutputConfig),
        type: '',
        type_ext: '',
      }
    : undefined;

/**
 * Converts a gRPC NodeInput to a REST NodeInput
 * @param nodeInput - The gRPC NodeInput object to convert
 * @returns The converted REST NodeInput object or undefined
 */
export const grpcToRestNodeInput = (nodeInput: NodeInput | undefined): { node_id: string } | undefined =>
  nodeInput
    ? {
        node_id: nodeInput.getNodeId(),
      }
    : undefined;

/**
 * Converts a gRPC WorkflowNode to a REST WorkflowNode
 * @param nodesEntity - The gRPC WorkflowNode object to convert
 * @returns The converted REST WorkflowNode object or undefined
 */
export const grpcToRestNodesEntity = (nodesEntity: WorkflowNode | undefined): CF.API.Workflows.NodesEntity | undefined => {
  const model = nodesEntity?.getModel();
  const restModel = model ? grpcToRestModel(model) : undefined;
  return nodesEntity
    ? {
        id: nodesEntity.getId(),
        node_inputs: nodesEntity
          .getNodeInputsList()
          .map(grpcToRestNodeInput)
          .filter((i) => !!i),
        model: {
          app_id: restModel ? restModel.app_id : '',
          display_name: restModel ? restModel.display_name || '' : '',
          id: restModel ? restModel.id : '',
          model_type_id: restModel ? restModel.model_type_id : '',
          model_version: { id: restModel ? restModel.model_version.id : '' },
          modified_at: null,
          name: restModel ? restModel.name : '',
          notes: restModel ? restModel.notes || '' : '',
          star_count: restModel ? restModel.star_count || 0 : 0,
          toolkits: restModel ? restModel.toolkits || [] : [],
          user_id: restModel ? restModel.user_id : '',
        },
        suppress_output: nodesEntity.getSuppressOutput(),
        output_info_override: grpcToRestOutputInfoWorkflow(nodesEntity.getOutputInfoOverride()),
      }
    : undefined;
};

const grpcToRestWorkflowVersion = (workflowVersion: WorkflowVersion | undefined): CF.API.Workflows.WorkflowVersion | undefined =>
  workflowVersion
    ? {
        id: workflowVersion.getId(),
        workflow_id: workflowVersion.getWorkflowId(),
        created_at: workflowVersion.getCreatedAt()?.toDate().toISOString() || undefined,
        modified_at: workflowVersion.getModifiedAt()?.toDate().toISOString() || undefined,
        visibility: grpcVisibilityToRest(workflowVersion.getVisibility()),
        nodes: workflowVersion
          .getNodesList()
          .map(grpcToRestNodesEntity)
          .filter((i) => !!i),
      }
    : undefined;

export const grpcToRestWorkflow = (workflow: Workflow): CF.API.Workflows.Workflow => {
  const metadata = workflow?.getMetadata();
  const visibility = grpcVisibilityToRest(workflow?.getVisibility());
  const version = grpcToRestWorkflowVersion(workflow?.getVersion());
  return {
    id: workflow.getId(),
    app_id: workflow.getAppId(),
    created_at: workflow.getCreatedAt()?.toDate().toISOString() || '',
    modified_at: workflow.getModifiedAt()?.toDate().toISOString() || '',
    description: workflow.getDescription(),
    // NOTE: Using empty object as fallback is intentional here
    // - Metadata is used for UI display and API querying
    // - Adding default values (like presetInputs) would confuse users about what's actually in the API
    // - Empty object is the safest fallback that won't break type checking
    metadata: metadata || ({} as CF.API.Metadata),
    visibility: visibility || { gettable: 0 },
    is_starred: workflow.getIsStarred(),
    star_count: workflow.getStarCount(),
    user_id: workflow.getUserId(),
    notes: workflow.getNotes(),
    image: grpcToRestImage(workflow?.getImage()),
    check_consents: workflow.getCheckConsentsList(),
    version: version || { id: '' },
    nodes: workflow
      .getNodesList()
      .map(grpcToRestNodesEntity)
      .filter((i) => !!i),
  };
};

export const grpcToRestAnnotation = (annotation: Annotation): CF.API.Annotations.Annotation | undefined => {
  const annotationStatus = annotation.getStatus();
  const annotationData = grpcToRestTOutputData(annotation.getData());
  if (!annotationData) {
    return;
  }

  const user = annotation.getWorker()?.getUser();
  const model = annotation.getWorker()?.getModel();
  const workflow = annotation.getWorker()?.getWorkflow();

  // eslint-disable-next-line consistent-return
  return {
    id: annotation.getId(),
    input_id: annotation.getInputId(),
    task_id: annotation.getTaskId(),
    data: annotationData,
    status: annotationStatus ? grpcToRestStatus(annotationStatus) : undefined,
    trusted: annotation.getTrusted(),
    modified_at: annotation.getModifiedAt()?.toString(),
    embed_model_version_id: annotation.getEmbedModelVersionId(),
    worker: {
      workflow: workflow ? grpcToRestWorkflow(workflow) : undefined,
      model: model ? grpcToRestModel(model) : undefined,
      user: user ? convertGrpcUserToRest(user) : undefined,
    },
  };
};

/**
 * Converts a gRPC Data to a REST Data
 * @param data - The gRPC Data object to convert
 * @returns The converted REST Data object
 */
function grpcToRestData(data: Data): CF.API.Inputs.AnyInputData {
  const metadata = data.getMetadata()?.toJavaScript();

  if (data.hasImage()) {
    const imageData = data.getImage();
    const image: CF.API.Inputs.ImageInputData['image'] = {
      url: imageData?.getUrl() || '',
      allow_duplicate_url: imageData?.getAllowDuplicateUrl() || false,
      hosted: imageData ? grpcToRestHostedUrl(imageData.getHosted()) : undefined,
      image_info: imageData ? grpcToRestImageInfo(imageData.getImageInfo()) : undefined,
      ...(imageData?.getBase64() ? { base64: imageData.getBase64() } : {}),
    };
    return { image, metadata };
  }

  if (data.hasVideo()) {
    const videoData = data.getVideo();
    const video: CF.API.Inputs.VideoInputData['video'] = {
      url: videoData?.getUrl() || '',
      allow_duplicate_url: videoData?.getAllowDuplicateUrl() || false,
      hosted: videoData ? grpcToRestHostedUrl(videoData.getHosted()) : undefined,
      thumbnail_url: videoData?.getThumbnailUrl(),
      video_info: videoData ? grpcToRestVideoInfo(videoData.getVideoInfo()) : undefined,
    };
    return { video, metadata };
  }

  if (data.hasText()) {
    const textData = data.getText();
    const text: CF.API.Inputs.TextInputData['text'] = {
      raw: textData?.getRaw() || '',
      url: textData?.getUrl() || '',
      allow_duplicate_url: textData?.getAllowDuplicateUrl() || false,
      hosted: textData ? grpcToRestHostedUrl(textData.getHosted()) : undefined,
      text_info: textData ? grpcToRestTextInfo(textData.getTextInfo()) : undefined,
    };
    return { text, metadata };
  }

  if (data.hasAudio()) {
    const audioData = data.getAudio();
    const audio: CF.API.Inputs.AudioInputData['audio'] = {
      url: audioData?.getUrl() || '',
      allow_duplicate_url: audioData?.getAllowDuplicateUrl() || false,
      hosted: audioData ? grpcToRestHostedUrl(audioData.getHosted()) : undefined,
    };
    return { audio, metadata };
  }

  // For unsupported data types, return an empty text input as a fallback
  return { text: { raw: '' }, metadata };
}

/**
 * Converts a gRPC HostedURL to a REST HostedData
 * @param hosted - The gRPC HostedURL object to convert
 * @returns The converted REST HostedData object or undefined
 */
function grpcToRestHostedUrl(hosted: HostedURL | undefined): CF.API.Inputs.HostedData | undefined {
  if (!hosted) {
    return undefined;
  }

  return {
    prefix: hosted.getPrefix(),
    suffix: hosted.getSuffix(),
    sizes: hosted.getSizesList(),
    crossorigin: hosted.getCrossorigin(),
  };
}

/**
 * Converts a gRPC RedirectInfo to a REST StatusRedirectInfo
 * @param re - The gRPC RedirectInfo object to convert
 * @returns The converted REST StatusRedirectInfo object or undefined
 */
function grpcToRestRedirectInfo(re: RedirectInfo | undefined): CF.API.StatusRedirectInfo | undefined {
  if (!re) {
    return undefined;
  }

  return {
    new_resource_id: re.getNewResourceId(),
    old_resource_id: re.getOldResourceId(),
    resource_type: re.getResourceType() as CF.API.RedirectedResourceType,
    url: re.getUrl(),
  };
}

/**
 * Converts a gRPC ImageInfo to a REST ImageInfo
 * @param info - The gRPC ImageInfo object to convert
 * @returns The converted REST ImageInfo object or undefined
 */
function grpcToRestImageInfo(info: ImageInfo | undefined): CF.API.Inputs.ImageData['image_info'] | undefined {
  if (!info) {
    return undefined;
  }

  return {
    color_mode: info.getColorMode(),
    format: info.getFormat(),
    height: info.getHeight(),
    width: info.getWidth(),
  };
}

/**
 * Converts a gRPC VideoInfo to a REST VideoInfo
 * @param info - The gRPC VideoInfo object to convert
 * @returns The converted REST VideoInfo object or undefined
 */
function grpcToRestVideoInfo(info: VideoInfo | undefined): CF.API.Inputs.VideoData['video_info'] | undefined {
  if (!info) {
    return undefined;
  }

  return {
    duration_seconds: info.getDurationSeconds(),
    fps: info.getFps(),
    frame_count: info.getFrameCount(),
    height: info.getHeight(),
    width: info.getWidth(),
    video_format: info.getVideoFormat(),
  };
}

/**
 * Converts a gRPC TextInfo to a REST TextInfo
 * @param info - The gRPC TextInfo object to convert
 * @returns The converted REST TextInfo object or undefined
 */
function grpcToRestTextInfo(info: TextInfo | undefined): CF.API.Inputs.TextData['text_info'] | undefined {
  if (!info) {
    return undefined;
  }

  return {
    char_count: info.getCharCount(),
    encoding: info.getEncoding(),
  };
}

/**
 * Converts a gRPC Status to a REST Status
 * @param status - The gRPC Status object to convert
 * @returns The converted REST Status object
 */
export const grpcToRestStatus = (status?: Status): CF.API.Status => {
  if (!status) {
    return {
      code: StatusCode.UNKNOWN,
      description: '',
    };
  }

  return {
    code: status.getCode(),
    description: status.getDescription(),
    details: status.getDetails(),
    req_id: status.getReqId(),
    stack_trace: status.getStackTraceList(),
    redirect_info: grpcToRestRedirectInfo(status.getRedirectInfo()),
  };
};

/**
 * Converts a gRPC Concept to a REST Concept
 * @param concept - The gRPC Concept object to convert
 * @returns The converted REST Concept object
 */
export const grpcToRestConcept = (concept: Concept): CF.API.Concept => ({
  id: concept.getId(),
  name: concept.getName(),
  app_id: concept.getAppId(),
  value: concept.getValue(),
  user_id: concept.getUserId(),
});

/**
 * Converts a gRPC ConceptRelation to a REST ConceptRelation
 * @param conceptRelation - The gRPC ConceptRelation object to convert
 * @returns The converted REST ConceptRelation object
 * @note This function currently has known issues with missing data handling.
 * The current implementation may mask underlying problems and needs to be re-evaluated.
 * TODO: Revisit data handling to properly handle missing/invalid data cases.
 */
export const grpcToRestConceptRelation = (conceptRelation: ConceptRelation): CF.API.Concepts.ConceptRelation => {
  const subjectConcept = conceptRelation.getSubjectConcept();
  const objectConcept = conceptRelation.getObjectConcept();

  return {
    id: conceptRelation.getId(),
    subjectConcept: subjectConcept ? grpcToRestConcept(subjectConcept) : { id: '', name: '' },
    objectConcept: objectConcept ? grpcToRestConcept(objectConcept) : { id: '', name: '' },
    predicate: conceptRelation.getPredicate() || '',
  };
};

const convertDefaultValue = (value?: string | number | boolean | number[] | null | CF.EmptyObject): string | number | boolean | number[] | null => {
  // Check if value is an empty object
  if (value !== null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
    return null; // Convert empty object to null
  }
  return value as string | number | boolean | number[] | null;
};

const convertModelTypeRangeInfo = (info?: CF.API.Models.ModelTypeRangeInfo | null): CF.API.Models.ModelTypeRangeInfo1 | null => {
  if (info === null || info === undefined) {
    return null;
  }

  // Handle max property - must ensure it's a number
  const maxValue = typeof info.max === 'string' ? Number.parseFloat(info.max) : info.max;

  // Check if parsing resulted in a valid number
  if (Number.isNaN(maxValue)) {
    throw new TypeError('Cannot convert string max value to a valid number');
  }

  return {
    max: maxValue,
    min: info.min,
    step: info.step,
  };
};

const grpcToRestModelTypeEnumOption = (modelTypeEnumOption: ModelTypeEnumOption): CF.API.Models.ModelTypeEnumOptionsEntity => {
  const fields = modelTypeEnumOption
    .getModelTypeFieldsList()
    .map(grpcToRestModelTypeField)
    .filter((i) => !!i);

  return {
    id: modelTypeEnumOption.getId(),
    description: modelTypeEnumOption.getDescription(),
    model_type_fields: fields.map((f) => ({
      path: f.path,
      field_type: f.field_type,
      default_value: convertDefaultValue(f.default_value),
      description: f.description || '',
      placeholder: f.placeholder,
      internal_only: f.internal_only,
      model_type_range_info: convertModelTypeRangeInfo(f.model_type_range_info),
      model_type_enum_options: f.model_type_enum_options,
    })),
    internal_only: modelTypeEnumOption.getInternalOnly(),
  };
};

/**
 * Converts a gRPC ModelTypeRangeInfo to a REST ModelTypeRangeInfo
 * @param modelTypeRangeInfo - The gRPC ModelTypeRangeInfo object to convert
 * @returns The converted REST ModelTypeRangeInfo object or undefined
 */
const grpcToRestModelTypeRangeInfo = (modelTypeRangeInfo: ModelTypeRangeInfo | undefined): CF.API.Models.ModelTypeRangeInfo | undefined =>
  modelTypeRangeInfo
    ? {
        max: modelTypeRangeInfo.getMax(),
        step: modelTypeRangeInfo.getStep(),
        min: modelTypeRangeInfo.getMin(),
      }
    : undefined;

/**
 * Converts a gRPC ModelTypeField to a REST ModelTypeField
 * @param modelTypeField - The gRPC ModelTypeField object to convert
 * @returns The converted REST ModelTypeField object or undefined
 */
const grpcToRestModelTypeField = (modelTypeField: ModelTypeField | undefined): CF.API.Models.ModelTypeFieldsEntity | undefined =>
  modelTypeField
    ? {
        path: modelTypeField.getPath(),
        field_type: modelTypeField.getFieldType(),
        description: modelTypeField.getDescription(),
        placeholder: modelTypeField.getPlaceholder(),
        required: modelTypeField.getRequired(),
        default_value: modelTypeField.getDefaultValue(),
        internal_only: modelTypeField.getInternalOnly(),
        model_type_range_info: grpcToRestModelTypeRangeInfo(modelTypeField.getModelTypeRangeInfo()),
        model_type_enum_options: modelTypeField
          .getModelTypeEnumOptionsList()
          .map(grpcToRestModelTypeEnumOption)
          .filter((i) => !!i),
      }
    : undefined;

/**
 * Converts a gRPC OutputConfig to a REST OutputConfig
 * @param outputConfig - The gRPC OutputConfig object to convert
 * @returns The converted REST OutputConfig object or undefined
 */
const grpcToRestOutputConfig = (outputConfig: OutputConfig | undefined): CF.API.Models.OutputConfig | undefined =>
  outputConfig
    ? {
        concepts_mutually_exclusive: outputConfig.getConceptsMutuallyExclusive(),
        max_concepts: outputConfig.getMaxConcepts(),
        min_value: outputConfig.getMinValue(),
      }
    : undefined;

/**
 * Converts a gRPC OutputConfig to a REST OutputConfig for models
 * @param outputConfig - The gRPC OutputConfig object to convert
 * @returns The converted REST OutputConfig object or undefined
 */
const grpcToRestModelsOutputConfig = (outputConfig: OutputConfig | undefined): CF.API.ModelOutputs.OutputConfig | undefined =>
  outputConfig
    ? {
        concepts_mutually_exclusive: outputConfig.getConceptsMutuallyExclusive(),
        closed_environment: false,
        max_concepts: outputConfig.getMaxConcepts(),
        min_value: outputConfig.getMinValue(),
      }
    : undefined;

/**
 * Converts a gRPC OutputInfo to a REST OutputInfo
 * @param outputInfo - The gRPC OutputInfo object to convert
 * @returns The converted REST OutputInfo object or undefined
 */
const grpcToRestOutputInfo = (outputInfo: OutputInfo | undefined): CF.API.Models.OutputInfo | undefined =>
  outputInfo
    ? {
        message: outputInfo.getMessage(),
        data: grpcToRestTOutputData(outputInfo.getData()),
        fields_map: outputInfo.getFieldsMap() as CF.API.Models.OutputInfoFieldsMap | undefined,
        params: outputInfo.getParams() as CF.API.Models.Params | undefined,
        params_specs: outputInfo
          .getParamsSpecsList()
          .map(grpcToRestModelTypeField)
          .filter((i) => !!i),
        output_config: grpcToRestOutputConfig(outputInfo.getOutputConfig()),
      }
    : undefined;

/**
 * Converts a gRPC MetricsSummary to a REST Summary
 * @param summary - The gRPC MetricsSummary object to convert
 * @returns The converted REST Summary object or undefined
 */
const grpcToRestSummary = (summary: MetricsSummary | undefined): CF.API.Models.Summary | undefined =>
  summary
    ? {
        top1_accuracy: summary.getTop1Accuracy(),
        top5_accuracy: summary.getTop5Accuracy(),
        macro_avg_roc_auc: summary.getMacroAvgRocAuc(),
        macro_std_roc_auc: summary.getMacroStdRocAuc(),
        macro_avg_f1_score: summary.getMacroAvgF1Score(),
        macro_std_f1_score: summary.getMacroStdF1Score(),
        macro_avg_precision: summary.getMacroAvgPrecision(),
        macro_avg_recall: summary.getMacroAvgRecall(),
        mean_avg_precision_iou_50: summary.getMeanAvgPrecisionIou50(),
      }
    : undefined;

/**
 * Converts a gRPC EvalMetrics to a REST Metrics
 * @param evalMetrics - The gRPC EvalMetrics object to convert
 * @returns The converted REST Metrics object
 */
const grpcToRestEvalMetrics = (evalMetrics: EvalMetrics): CF.API.Models.Metrics => {
  const status = grpcToRestStatus(evalMetrics.getStatus());
  const summary = grpcToRestSummary(evalMetrics.getSummary());

  return {
    status,
    summary: {
      top1_accuracy: summary ? summary.top1_accuracy : undefined,
      top5_accuracy: summary ? summary.top5_accuracy : undefined,
      macro_avg_roc_auc: summary ? summary.macro_avg_roc_auc : 0,
      macro_std_roc_auc: summary ? summary.macro_std_roc_auc : 0,
      macro_avg_f1_score: summary ? summary.macro_avg_f1_score : 0,
      macro_std_f1_score: summary ? summary.macro_std_f1_score : 0,
      macro_avg_precision: summary ? summary.macro_avg_precision : 0,
      macro_avg_recall: summary ? summary.macro_avg_recall : 0,
      mean_avg_precision_iou_50: summary ? summary.mean_avg_precision_iou_50 : undefined,
    },
  };
};

/**
 * Gets a stable OutputInfo object
 * @param outputInfo - The OutputInfo object to stabilize
 * @returns A stable OutputInfo object
 */
const getStableOutputInfo = (outputInfo?: CF.API.Models.OutputInfo): CF.API.Models.OutputInfo => ({
  ...outputInfo,
  message: outputInfo?.message || '',
  data: outputInfo?.data || undefined,
  fields_map: outputInfo?.fields_map || undefined,
  params: outputInfo?.params || undefined,
  params_specs: outputInfo?.params_specs || [],
  output_config: outputInfo?.output_config || undefined,
});

/**
 * Gets a stable Status object
 * @param status - The Status object to stabilize
 * @returns A stable Status object
 */
const getStableStatus = (status?: CF.API.Status): CF.API.Status => ({
  ...status,
  code: status?.code || StatusCode.UNKNOWN,
  description: status?.description || '',
  details: status?.details || undefined,
  req_id: status?.req_id || undefined,
  stack_trace: status?.stack_trace || [],
  redirect_info: status?.redirect_info || undefined,
});

/**
 * Gets a stable Visibility object
 * @param visibility - The Visibility object to stabilize
 * @returns A stable Visibility object
 */
const getStableVisibility = (visibility?: CF.API.Visibility): CF.API.Visibility => ({
  ...visibility,
  gettable: visibility?.gettable || 0, // 0 represents UNKNOWN_VISIBILITY
});

/**
 * Converts a gRPC InputInfo to a REST InputInfo
 * @param inputInfo - The gRPC InputInfo object to convert
 * @returns The converted REST InputInfo object
 */
const grpcToRestInputInfo = (inputInfo: InputInfo): CF.API.Models.InputInfo => {
  const model = inputInfo.getBaseEmbedModel();
  const fieldsMap = inputInfo.getFieldsMap()?.getFieldsMap();

  // The fields map is a Map<string, unknown> from the gRPC layer, but we know
  // the specific fields we're interested in are strings. We extract them here
  // and let TypeScript infer the correct types based on the InputInfoFieldsMap interface.
  const image = fieldsMap?.get('image')?.toString() || undefined;
  const audio = fieldsMap?.get('audio')?.toString() || undefined;
  const text = fieldsMap?.get('text')?.toString() || undefined;

  const hasFieldsMap = fieldsMap || image || audio || text;

  return {
    fields_map: hasFieldsMap
      ? {
          image,
          audio,
          text,
        }
      : undefined,
    base_embed_model: model ? grpcToRestModel(model) : undefined,
  };
};

/**
 * Converts a gRPC Search to a REST Search
 * @param search - The gRPC Search object to convert
 * @returns The converted REST Search object or undefined
 */
export const grpcToRestSearch = (search: Search | undefined): CF.API.Search.Search | undefined => {
  if (!search) {
    return undefined;
  }

  const id = search.getId() || '';
  const name = search.getName() || '';
  const application_id = search.getApplicationId() || '';
  const created_at = search.getCreatedAt()?.toDate().toISOString() || '';
  const modified_at = search.getModifiedAt()?.toDate().toISOString() || '';
  const algorithm = search.getAlgorithm() || '';
  const as_of = search.getAsOf()?.toDate().toISOString() || undefined;
  const min_value = search.getMinValue();
  const save = search.getSave();
  const git_hash = search.getGitHash() || '';

  const gettable = search.getVisibility()?.getGettable();
  const visibility = gettable ? { gettable } : undefined;

  const searchQuery = search.getQuery();

  if (!searchQuery) {
    return {
      id,
      application_id,
      name,
      algorithm,
      query: {
        ands: [],
        filters: [],
        ranks: [],
      },
      ...(created_at ? { created_at } : {}),
      ...(modified_at ? { modified_at } : {}),
      ...(as_of ? { as_of } : {}),
      ...(git_hash ? { git_hash } : {}),
      ...(visibility ? { visibility } : {}),
      ...(min_value ? { min_value } : {}),
      ...(save ? { save } : {}),
    };
  }

  const searchAndsList = searchQuery.getAndsList();
  const searchFiltersList = searchQuery.getFiltersList();

  const query = {
    ands: grpcToRestAnds(searchAndsList),
    filters: searchFiltersList.map((filter) => grpcToRestFilter(filter)),
    ranks: [],
  };

  return {
    id,
    application_id,
    name,
    algorithm,
    query,
    ...(created_at ? { created_at } : {}),
    ...(modified_at ? { modified_at } : {}),
    ...(as_of ? { as_of } : {}),
    ...(git_hash ? { git_hash } : {}),
    ...(visibility ? { visibility } : {}),
    ...(min_value ? { min_value } : {}),
    ...(save ? { save } : {}),
    ...(visibility ? { visibility } : {}),
  };
};

/**
 * Converts a gRPC And array to a REST And array
 * @param ands - The gRPC And array to convert
 * @returns The converted REST And array
 */
const grpcToRestAnds = (ands: Array<And>) => {
  return ands?.map((and) => {
    const andInput = and.getInput();
    const andOutput = and.getOutput();
    const andAnnotation = and.getAnnotation();

    return {
      input: andInput ? grpcToRestInputOptional(andInput) : undefined,
      output: andOutput ? grpcToRestOutput(andOutput) : undefined,
      negate: and.getNegate(),
      annotation: andAnnotation ? grpcToRestAnnotation(andAnnotation) : undefined,
    };
  });
};

/**
 * Converts a gRPC Filter to a REST Filter
 * @param filter - The gRPC Filter object to convert
 * @returns The converted REST Filter object
 */
const grpcToRestFilter = (filter: Filter) => {
  const filterInput = filter.getInput();
  const filterAnnotation = filter.getAnnotation();
  const lastUpdatedTimeRange = filter.getLastUpdatedTimeRange();
  const startTime = lastUpdatedTimeRange?.getStartTime()?.toDate().toISOString() || undefined;
  const endTime = lastUpdatedTimeRange?.getEndTime()?.toDate().toISOString() || undefined;

  return {
    input: filterInput ? grpcToRestInputOptional(filterInput) : undefined,
    negate: filter.getNegate(),
    annotation: filterAnnotation ? grpcToRestAnnotation(filterAnnotation) : undefined,
    lastUpdatedTimeRange: startTime || endTime ? { startTime, endTime } : undefined,
  };
};

/**
 * Converts a gRPC Timestamp to a string
 * @param timestamp - The gRPC Timestamp object to convert
 * @returns The converted string or empty string if timestamp is undefined
 */
export const grpcTimestampToString = (timestamp?: Timestamp) => {
  if (!timestamp) {
    return '';
  }

  return timestamp.toDate().toISOString();
};

/**
 * Converts a gRPC Visibility to a REST Visibility
 * @param visibility - The gRPC Visibility object to convert
 * @returns The converted REST Visibility object or undefined
 */
export const grpcVisibilityToRest = (visibility?: Visibility): CF.API.Visibility | undefined => {
  if (!visibility) {
    return undefined;
  }

  return {
    gettable: visibility.getGettable(),
  };
};

/**
 * Converts a gRPC EmailAddress to a REST EmailAddress
 * @param emailAddress - The gRPC EmailAddress object to convert
 * @returns The converted REST EmailAddress object or undefined
 */
export const grpcEmailAddressToRest = (emailAddress?: EmailAddress): CF.API.Users.EmailAddressesEntity | undefined => {
  if (!emailAddress) {
    return undefined;
  }

  return {
    email: emailAddress.getEmail(),
    primary: emailAddress.getPrimary(),
    verified: emailAddress.getVerified(),
  };
};

/**
 * Converts a gRPC UserDetail to a REST UserDetail
 * @param userDetail - The gRPC UserDetail object to convert
 * @returns The converted REST UserDetail object or undefined
 */
export const grpcUserDetailToRest = (userDetail?: UserDetail): CF.API.Users.UserDetail | undefined => {
  if (!userDetail) {
    return undefined;
  }

  return {
    primary_email: userDetail.getPrimaryEmail(),
    bill_type: userDetail.getBillType(),
    date_gdpr_consent: grpcTimestampToString(userDetail.getDateGdprConsent()),
    date_tos_consent: grpcTimestampToString(userDetail.getDateTosConsent()),
    date_marketing_consent: grpcTimestampToString(userDetail.getDateMarketingConsent()),
    date_pii_consent: grpcTimestampToString(userDetail.getDatePiiConsent()),
    metadata: userDetail.getMetadata(),
    email_addresses: userDetail.getEmailAddressesList().map(grpcEmailAddressToRest).filter(Boolean) as CF.API.Users.EmailAddressesEntity[],
    country: userDetail.getCountry(),
    state: userDetail.getState(),
  };
};

/**
 * Converts a gRPC User to a REST User
 * @param user - The gRPC User object to convert
 * @returns The converted REST User object
 */
export const convertGrpcUserToRest = (user: User): CF.API.Users.User => ({
  id: user.getId(),
  primary_email: user.getPrimaryEmail(),
  first_name: user.getFirstName(),
  last_name: user.getLastName(),
  company_name: user.getCompanyName(),
  bill_type: user.getBillType(),
  created_at: grpcTimestampToString(user.getCreatedAt()),
  date_gdpr_consent: grpcTimestampToString(user.getDateGdprConsent()),
  date_tos_consent: grpcTimestampToString(user.getDateTosConsent()),
  date_marketing_consent: grpcTimestampToString(user.getDateMarketingConsent()),
  metadata: user.getMetadata(),
  email_addresses: user.getEmailAddressesList().map(grpcEmailAddressToRest).filter(Boolean) as CF.API.Users.EmailAddressesEntity[],
  two_factor_auth_enabled: user.getTwoFactorAuthEnabled(),
  teams_count: user.getTeamsCount(),
  job_title: user.getJobTitle(),
  job_role: user.getJobRole(),
  intention: user.getIntention(),
  visibility: grpcVisibilityToRest(user.getVisibility()),
  user_detail: grpcUserDetailToRest(user.getUserDetail()),
});

/**
 * Converts a gRPC TaskStatusCountPerUser to a REST TaskInputCount
 * @param counter - The gRPC TaskStatusCountPerUser object to convert
 * @returns The converted REST TaskInputCount object
 */
export const convertGrpcTaskCounterToRest = (counter: TaskStatusCountPerUser): CF.API.TaskInputCount => ({
  awaiting_consensus_review: counter.getAwaitingConsensusReview() || 0,
  awaiting_review: counter.getAwaitingReview() || 0,
  pending: counter.getPending() || 0,
  review_denied: counter.getReviewDenied() || 0,
  success: counter.getSuccess() || 0,
  user_id: counter.getUserId() || '',
});

/**
 * Converts a protobuf Value to JavaScript value
 * @param value - The protobuf Value to convert
 * @returns The converted JavaScript value
 */
const protoValueToJs = (value: google_protobuf_struct_pb.Value): unknown => {
  if (!value) {
    return null;
  }

  if (value.hasNullValue()) {
    return null;
  } else if (value.hasNumberValue()) {
    return value.getNumberValue();
  } else if (value.hasStringValue()) {
    return value.getStringValue();
  } else if (value.hasBoolValue()) {
    return value.getBoolValue();
  } else if (value.hasStructValue()) {
    return protoStructToRecord(value.getStructValue() ?? new google_protobuf_struct_pb.Struct());
  } else if (value.hasListValue()) {
    const listValue = value.getListValue() ?? new google_protobuf_struct_pb.ListValue();
    const values = listValue.getValuesList();
    return values.map((v) => protoValueToJs(v));
  }

  return undefined;
};

/**
 * Converts a protobuf Struct to a JavaScript Record
 * @param struct - The protobuf Struct to convert
 * @returns The converted JavaScript Record
 */
export const protoStructToRecord = (struct: google_protobuf_struct_pb.Struct): Record<string, unknown> => {
  if (!struct) {
    return {};
  }

  const record: Record<string, unknown> = {};
  const fieldsMap = struct.getFieldsMap();

  if (!fieldsMap) {
    return record;
  }

  fieldsMap.forEach((value, key) => {
    if (key && value) {
      record[key] = protoValueToJs(value);
    }
  });

  return record;
};

/**
 * Converts a gRPC Output to a REST Output
 * @param output - The gRPC Output object to convert
 * @returns The converted REST Output object
 */
const grpcToRestOutput = (output: Output) => {
  const outputInput = output.getInput();
  const outputModel = output.getModel();
  const outputData = output.getData();
  const outputStatus = output.getStatus();

  return {
    id: output.getId(),
    status: outputStatus ? outputStatus.toObject() : undefined,
    model: outputModel ? grpcToRestModel(outputModel) : undefined,
    created_at: output.getCreatedAt()?.toDate().toISOString() || undefined,
    input: outputInput ? grpcToRestInput(outputInput) : undefined,
    // NOTE: Using empty object as fallback is intentional here
    // - We don't want to assume a specific type as it might be a type we don't handle yet
    // - This allows the conversion to succeed even with broken/missing data from backend
    // - The empty object is the safest fallback that won't break type checking
    data: outputData ? grpcToRestData(outputData) : ({} as CF.API.Inputs.AnyInputData),
  };
};

/**
 * Gets a stable ModelVersion object
 * @param modelVersion - The ModelVersion object to stabilize
 * @returns A stable ModelVersion object
 */
const getStableModelVersion = (modelVersion?: CF.API.Models.ModelVersion): CF.API.Models.ModelVersion => ({
  ...modelVersion,
  id: modelVersion?.id || '',
  created_at: modelVersion?.created_at || '',
  status: getStableStatus(modelVersion?.status),
  visibility: getStableVisibility(modelVersion?.visibility),
  app_id: modelVersion?.app_id || '',
  user_id: modelVersion?.user_id || '',
  // NOTE: Using empty object as fallback is intentional here
  // - Metadata is used for UI display and API querying
  // - Adding default values would confuse users about what's actually in the API
  // - Empty object is the safest fallback that won't break type checking
  metadata: modelVersion?.metadata || ({} as CF.API.Models.MetadataOrTrainInfoOrInputInfo),
  description: modelVersion?.description || '',
  active_concept_count: modelVersion?.active_concept_count || 0,
  metrics: modelVersion?.metrics || undefined,
  license: modelVersion?.license || undefined,
  train_log: modelVersion?.train_log || undefined,
  input_info: modelVersion?.input_info || undefined,
  output_info: modelVersion?.output_info || undefined,
  train_info: modelVersion?.train_info || undefined,
  inference_compute_info: modelVersion?.inference_compute_info || undefined,
});

/**
 * Converts a gRPC ModelVersion to a REST ModelVersion
 * @param modelVersion - The gRPC ModelVersion object to convert
 * @returns The converted REST ModelVersion object
 */
const grpcToRestModelVersion = (modelVersion: ModelVersion): CF.API.Models.ModelVersion => {
  const visibility = grpcVisibilityToRest(modelVersion?.getVisibility());
  const metadata = modelVersion?.getMetadata();
  const metrics = modelVersion?.getMetrics();
  const inputInfo = modelVersion?.getInputInfo();

  return {
    id: modelVersion.getId(),
    created_at: modelVersion.getCreatedAt()?.toDate().toISOString() || '',
    status: grpcToRestStatus(modelVersion.getStatus()),
    visibility: visibility || { gettable: 0 },
    app_id: modelVersion.getAppId(),
    user_id: modelVersion.getUserId(),
    description: modelVersion.getDescription(),
    // NOTE: Using empty object as fallback is intentional here
    // - Metadata is used for UI display and API querying
    // - Adding default values would confuse users about what's actually in the API
    // - Empty object is the safest fallback that won't break type checking
    metadata: metadata ? protoStructToRecord(metadata) : ({} as CF.API.Models.MetadataOrTrainInfoOrInputInfo),
    active_concept_count: modelVersion.getActiveConceptCount(),
    metrics: metrics ? grpcToRestEvalMetrics(metrics) : undefined,
    license: modelVersion.getLicense(),
    train_log: modelVersion.getTrainLog(),
    input_info: inputInfo ? grpcToRestInputInfo(inputInfo) : undefined,
    output_info: grpcToRestOutputInfo(modelVersion?.getOutputInfo()),
    train_info: undefined,
    inference_compute_info: undefined,
  };
};

/**
 * Converts a gRPC Model to a REST Model
 * @param model - The gRPC Model object to convert
 * @returns The converted REST Model object
 */
const grpcToRestModel = (model: Model): CF.API.Models.Model => {
  const modelVersion = model.getModelVersion();
  const restModelVersion = modelVersion ? grpcToRestModelVersion(modelVersion) : undefined;
  const visibility = model.getVisibility();
  const modelVisibility = visibility ? grpcVisibilityToRest(visibility) : undefined;
  const metadata = model.getMetadata();

  return {
    id: model.getId(),
    name: model.getName(),
    display_name: model.getDisplayName(),
    notes: model.getNotes(),
    created_at: model.getCreatedAt()?.toDate().toISOString() || '',
    modified_at: model.getModifiedAt()?.toDate().toISOString() || '',
    app_id: model.getAppId(),
    output_info: getStableOutputInfo(grpcToRestOutputInfo(model.getOutputInfo())),
    model_version: getStableModelVersion(restModelVersion),
    user_id: model.getUserId(),
    model_type_id: model.getModelTypeId(),
    visibility: getStableVisibility(modelVisibility),
    // NOTE: Using empty object as fallback is intentional here
    // - Metadata is used for UI display and API querying
    // - Adding default values would confuse users about what's actually in the API
    // - Empty object is the safest fallback that won't break type checking
    metadata: metadata ? protoStructToRecord(metadata) : ({} as CF.API.Metadata),
    presets: model.getPresets(),
    image: grpcToRestImage(model?.getImage()),
    description: model.getDescription(),
    use_cases: model.getUseCasesList(),
    toolkits: model.getToolkitsList(),
    is_starred: model.getIsStarred(),
    star_count: model.getStarCount(),
    workflow_recommended: !!model.getWorkflowRecommended(),
    check_consents: model.getCheckConsentsList(),
  };
};
